import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api';
import type { LoginResponse } from '@/types/auth';
import { useAuthStore } from '@/stores/authStore';
import { useMdmContextStore } from '@/stores/mdmContextStore';

/**
 * Cliente HTTP central.
 *
 * Headers que envia automaticamente:
 * - Authorization: Bearer <accessToken>     (siempre que haya token)
 * - X-Mdm-Id: <id>                          (si hay un MDM seleccionado en el header del panel)
 *
 * Manejo de 401 con refresh token automatico (encola requests pendientes).
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ===== Request interceptor: inyectar access token + X-Mdm-Id =====
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = useAuthStore.getState().accessToken;
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // [3-tier] Header con el MDM seleccionado (para que el backend filtre)
  const currentMdmId = useMdmContextStore.getState().currentMdmId;
  if (currentMdmId != null && config.headers) {
    config.headers['X-Mdm-Id'] = String(currentMdmId);
  }

  return config;
});

// ===== Response interceptor: manejo de 401 y refresh =====

let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null) {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Si no es 401 o ya reintentamos, propagar el error
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // No reintentar para los endpoints de auth (login, refresh)
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    const { refreshToken, setTokens, clear } = useAuthStore.getState();
    if (!refreshToken) {
      clear();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      // Hay un refresh en curso, encolar esta request
      return new Promise((resolve, reject) => {
        pendingQueue.push((newToken) => {
          if (newToken) {
            originalRequest.headers!.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await axios.post<ApiResponse<LoginResponse>>(
        `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (!refreshResponse.data.success || !refreshResponse.data.data) {
        throw new Error('Refresh fallido');
      }

      const { accessToken: newAccess, refreshToken: newRefresh, user } = refreshResponse.data.data;
      setTokens(newAccess, newRefresh, user);
      flushQueue(newAccess);

      originalRequest.headers!.Authorization = `Bearer ${newAccess}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      flushQueue(null);
      clear();
      // Redirigir a login
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/**
 * Helper para extraer el data de la respuesta y manejar el wrapper ApiResponse.
 * Lanza error si success === false.
 */
export async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const response = await promise;
  if (!response.data.success) {
    throw new Error(response.data.message || 'Error en la solicitud');
  }
  if (response.data.data === undefined) {
    throw new Error('La respuesta no contiene datos');
  }
  return response.data.data;
}
